import { useState, useRef } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  Upload,
  Phone,
  FileAudio,
  Loader2,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  ThumbsUp,
  Lightbulb,
  Clock,
  MessageSquare,
  Download,
  Share2,
  Copy,
  Check,
  Link as LinkIcon
} from "lucide-react";

export default function CallAnalysis() {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const fileInputRef = useRef(null);
  const resultsRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    const validTypes = ["audio/mp3", "audio/mpeg", "audio/wav", "audio/webm", "audio/m4a", "audio/mp4"];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|webm|m4a|mp4|mpeg)$/i)) {
      toast.error("Invalid file type", {
        description: "Please upload an audio file (MP3, WAV, WebM, M4A)"
      });
      return;
    }

    // Check file size (max 25MB)
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large", {
        description: "Maximum file size is 25MB"
      });
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/analyze/upload`, {
        method: "POST",
        credentials: "include",
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Analysis failed");
      }

      const data = await response.json();
      setResult(data);
      toast.success("Analysis complete!");
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Failed to analyze call", {
        description: error.message
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  // Export to PDF
  const handleExportPDF = async () => {
    if (!resultsRef.current) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(resultsRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff"
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
      });
      
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`call-analysis-${result.analysis_id}.pdf`);
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  // Create share link
  const handleCreateShareLink = async () => {
    if (!result?.analysis_id) return;
    
    setShareLoading(true);
    try {
      const response = await fetch(`${API}/analyze/${result.analysis_id}/share`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        const link = `${window.location.origin}/shared/analysis/${data.token}`;
        setShareLink(link);
        toast.success("Share link created!");
      } else {
        throw new Error("Failed to create share link");
      }
    } catch (error) {
      console.error("Share failed:", error);
      toast.error("Failed to create share link");
    } finally {
      setShareLoading(false);
    }
  };

  // Copy link to clipboard
  const handleCopyLink = async () => {
    if (!shareLink) return;
    
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="call-analysis-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">Call Analysis</h1>
        <p className="text-muted-foreground mt-1">
          Upload a sales call recording for AI-powered feedback
        </p>
      </div>

      {/* Upload Area */}
      <Card className={`transition-all ${dragActive ? "border-primary border-2" : ""}`}>
        <CardContent className="p-8">
          <div
            className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
              dragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25"
            }`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
          >
            {uploading ? (
              <div className="space-y-4">
                <Loader2 className="w-16 h-16 text-primary mx-auto animate-spin" />
                <div>
                  <p className="font-medium">Analyzing your call...</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Transcribing and generating feedback
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <FileAudio className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">
                  Drop your audio file here
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  or click to browse. Supports MP3, WAV, WebM, M4A (max 25MB)
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/*"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                  className="hidden"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Select File
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <div className="space-y-6" data-testid="analysis-results">
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 justify-end">
            {/* Export PDF Button */}
            <Button 
              variant="outline" 
              onClick={handleExportPDF}
              disabled={exporting}
              data-testid="export-pdf-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export PDF
            </Button>

            {/* Share Dialog */}
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="share-analysis-btn">
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Share Analysis</DialogTitle>
                  <DialogDescription>
                    Create a shareable link that allows others to view this analysis without logging in.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  {!shareLink ? (
                    <Button 
                      onClick={handleCreateShareLink} 
                      className="w-full"
                      disabled={shareLoading}
                    >
                      {shareLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <LinkIcon className="w-4 h-4 mr-2" />
                      )}
                      Generate Share Link
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Input 
                          value={shareLink} 
                          readOnly 
                          className="font-mono text-sm"
                        />
                        <Button 
                          size="icon" 
                          variant="outline"
                          onClick={handleCopyLink}
                        >
                          {copied ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Anyone with this link can view the analysis.
                      </p>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Results Content - wrapped in ref for PDF export */}
          <div ref={resultsRef} className="space-y-6 bg-background p-4">
            {/* Score Overview */}
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="relative overflow-hidden">
                <div className={`absolute top-0 left-0 right-0 h-1 ${
                  result.analysis.overall_score >= 80 ? "bg-green-500" :
                  result.analysis.overall_score >= 60 ? "bg-yellow-500" : "bg-red-500"
                }`} />
                <CardContent className="p-6 text-center">
                  <CheckCircle className={`w-10 h-10 mx-auto mb-3 ${getScoreColor(result.analysis.overall_score)}`} />
                  <p className={`font-heading text-5xl font-bold ${getScoreColor(result.analysis.overall_score)}`}>
                    {result.analysis.overall_score}
                  </p>
                  <p className="text-muted-foreground mt-1">Overall Score</p>
                </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Analysis Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{result.analysis.summary}</p>
                <div className="flex gap-2 mt-4">
                  {result.analysis.call_type && (
                    <Badge variant="secondary">
                      <Phone className="w-3 h-3 mr-1" />
                      {result.analysis.call_type.replace(/_/g, " ")}
                    </Badge>
                  )}
                  {result.analysis.duration_estimate && (
                    <Badge variant="outline">
                      <Clock className="w-3 h-3 mr-1" />
                      {result.analysis.duration_estimate}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Metrics */}
          {result.analysis.metrics && Object.keys(result.analysis.metrics).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Performance Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Object.entries(result.analysis.metrics).map(([key, value]) => (
                    <div key={key} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium capitalize">
                          {key.replace(/_/g, " ")}
                        </span>
                        <span className={`font-mono font-bold ${getScoreColor(value)}`}>
                          {value}%
                        </span>
                      </div>
                      <Progress value={value} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Strengths & Improvements */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-green-500">
                  <ThumbsUp className="w-5 h-5" />
                  Strengths
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {result.analysis.strengths?.map((s, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
                      <span className="text-sm">{s}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-yellow-500">
                  <Lightbulb className="w-5 h-5" />
                  Areas to Improve
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {result.analysis.areas_for_improvement?.map((a, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <div className="w-2 h-2 rounded-full bg-yellow-500 mt-2" />
                      <span className="text-sm">{a}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Transcript */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" />
                Transcript
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                  {result.transcript}
                </p>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Recommendations */}
          {result.analysis.recommendations?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recommendations</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {result.analysis.recommendations.map((rec, i) => (
                    <li key={i} className="flex items-center gap-3 p-3 rounded-lg bg-primary/5">
                      <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <span className="text-sm">{rec}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          </div>
        </div>
      )}
    </div>
  );
}
